import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { sans } from '../Vendors/VendorDetails';
import {
  QItem, TreeNode, FlatRow, QStatus, SidebarFilter, buildSections, flattenNodes, hiddenChildrenOf, indexTree,
  isNegativeAnswer, codeOf, qPalette, QPalette, QuestionnaireSidebar, QuestionFrame, AnswerControl, ReasonField,
  SectionHeader, SectionPager, Chip, TopBar, ProgressBlock, FilteredList, EmptySection,
} from './questionnaireShell';

interface QuestionnaireData {
  id: string;
  name: string;
  description: string | null;
  item_count: number;
  items: QItem[];
  section_titles?: Record<string, string> | null;
}

type FilterKey = 'all' | 'unanswered' | 'needs-note' | 'negative' | 'followups';

const TOP = 64;

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};

// Platform-wide, vendor-agnostic preview of a Questionnaire Library entry -- lets anyone click
// through it to see how its follow-up (hidden) questions reveal, laid out exactly like the vendor
// portal's real answering page (both use ./questionnaireShell.tsx). Nothing typed here is saved
// anywhere; answers live only in this component's state for the length of the visit.
export default function QuestionnairePreview() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const p = qPalette(isDark);

  const [data, setData] = useState<QuestionnaireData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [evidence, setEvidence] = useState<Record<string, string[]>>({});
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [sectionKey, setSectionKey] = useState<string | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingScroll = useRef<string | null>(null);

  useEffect(() => {
    if (!questionnaireId) return;
    api.get(`/v1/questionnaire-library/${questionnaireId}`)
      .then(({ data }) => setData(data))
      .catch(err => setLoadError(apiError(err, 'Could not load this questionnaire.')));
  }, [questionnaireId]);

  const items = useMemo(() => data?.items || [], [data]);
  const itemByKey = useMemo(() => Object.fromEntries(items.map(i => [i.question_key, i])), [items]);
  const sections = useMemo(() => buildSections(items, answers), [items, answers]);
  const hidden = useMemo(() => hiddenChildrenOf(items), [items]);
  const allFlat = useMemo(() => sections.flatMap(s => flattenNodes(s.nodes)), [sections]);
  const { sectionOfKey, parentOfKey } = useMemo(() => indexTree(sections), [sections]);

  const isAnswered = (key: string) => !!(answers[key] || '').trim();
  const needsNote = (key: string) => isNegativeAnswer(answers[key]) && !(notes[key] || '').trim();
  const statusOf = (item: QItem): QStatus =>
    needsNote(item.question_key) ? 'needs-note' : isAnswered(item.question_key) ? 'answered' : 'unanswered';

  const answeredCount = allFlat.filter(r => isAnswered(r.item.question_key)).length;

  const matchesFilter = (r: FlatRow<QItem>, f: FilterKey) => {
    const k = r.item.question_key;
    if (f === 'unanswered') return !isAnswered(k);
    if (f === 'needs-note') return needsNote(k);
    if (f === 'negative') return isNegativeAnswer(answers[k]);
    if (f === 'followups') return r.depth > 0;
    return true;
  };
  const count = (f: FilterKey) => allFlat.filter(r => matchesFilter(r, f)).length;
  const filters: SidebarFilter[] = [
    { key: 'all', label: 'All', count: allFlat.length, tone: 'blue' },
    { key: 'unanswered', label: 'Unanswered', count: count('unanswered'), tone: 'slate' },
    { key: 'needs-note', label: 'No reason yet', count: count('needs-note'), tone: 'amber' },
    { key: 'negative', label: 'No / N/A', count: count('negative'), tone: 'slate' },
    { key: 'followups', label: 'Follow-ups', count: count('followups'), tone: 'purple' },
  ];

  const filtering = search.trim() !== '' || filter !== 'all';
  const filtered = useMemo(() => {
    if (!filtering) return null;
    const q = search.trim().toLowerCase();
    return allFlat.filter(r => (!q || r.item.question_text.toLowerCase().includes(q)) && matchesFilter(r, filter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFlat, search, filter, answers, notes, filtering]);

  const current = sections.find(s => s.key === sectionKey) || sections[0];
  const currentIdx = current ? sections.indexOf(current) : 0;
  const sectionTitle = (key: string) => data?.section_titles?.[key] || `Section ${key}`;

  // Scroll only after the target section has rendered -- a jump into another section switches the
  // section first, then scrolls on the next paint.
  useEffect(() => {
    const key = pendingScroll.current;
    if (!key) return;
    pendingScroll.current = null;
    requestAnimationFrame(() => itemRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }, [sectionKey, activeKey, filtering]);

  const jumpTo = (key: string) => {
    setActiveKey(key);
    if (!filtering && sectionOfKey[key]) setSectionKey(sectionOfKey[key]);
    pendingScroll.current = key;
  };

  const selectSection = (key: string) => {
    setSectionKey(key);
    setSearch('');
    setFilter('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setAnswer = (key: string, value: string) => setAnswers(prev => ({ ...prev, [key]: value }));
  const setNote = (key: string, value: string) => setNotes(prev => ({ ...prev, [key]: value }));
  const addEvidenceFile = (key: string, fileName: string) => setEvidence(prev => ({ ...prev, [key]: [...(prev[key] || []), fileName] }));
  const removeEvidenceFile = (key: string, idx: number) => setEvidence(prev => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));

  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: p.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 13, color: p.red }}>{loadError}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ minHeight: '100vh', background: p.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 12, color: p.muted }}>Loading questionnaire…</div>
      </div>
    );
  }

  const card = (item: QItem, depth: number, parentCode: string | null) => {
    const key = item.question_key;
    const value = answers[key] || '';
    return (
      <QuestionFrame
        key={item.id}
        p={p}
        font={sans}
        item={item}
        depth={depth}
        parentCode={parentCode}
        active={activeKey === key}
        needsNote={needsNote(key)}
        setRef={el => { itemRefs.current[key] = el; }}
        onFocus={() => setActiveKey(key)}
      >
        <AnswerControl p={p} font={sans} item={item} value={value} onChange={v => setAnswer(key, v)} onFocus={() => setActiveKey(key)} />
        {isNegativeAnswer(value) && (
          <ReasonField p={p} font={sans} value={notes[key] || ''} onChange={v => setNote(key, v)} onFocus={() => setActiveKey(key)} />
        )}
        <PreviewEvidence
          p={p}
          itemKey={key}
          files={evidence[key] || []}
          onAdd={name => addEvidenceFile(key, name)}
          onRemove={idx => removeEvidenceFile(key, idx)}
        />
      </QuestionFrame>
    );
  };

  const renderNode = (node: TreeNode<QItem>, depth: number, parentCode: string | null): React.ReactNode => (
    <React.Fragment key={node.item.id}>
      {card(node.item, depth, parentCode)}
      {node.children.map(child => renderNode(child, depth + 1, codeOf(node.item)))}
    </React.Fragment>
  );

  const parentCodeOf = (key: string) => {
    const parentKey = parentOfKey[key];
    return parentKey && itemByKey[parentKey] ? codeOf(itemByKey[parentKey]) : null;
  };

  const currentFlat = current ? flattenNodes(current.nodes) : [];
  const currentDone = currentFlat.filter(r => statusOf(r.item) !== 'unanswered').length;

  return (
    <div style={{ minHeight: '100vh', background: p.bg, fontFamily: sans, fontSize: 15 }}>
      <TopBar p={p} height={TOP}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <button
            type="button"
            onClick={() => navigate('/questionnaire-library')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: p.blue, fontFamily: sans, padding: 0, fontWeight: 600 }}
          >
            ← Questionnaire Library
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.name}</div>
            <Chip p={p} tone="blue">Preview · answers aren't saved</Chip>
          </div>
        </div>
        <ProgressBlock p={p} done={answeredCount} total={allFlat.length} />
      </TopBar>

      <div style={{ display: 'flex' }}>
        <QuestionnaireSidebar
          p={p}
          font={sans}
          top={TOP}
          sections={sections}
          sectionTitle={sectionTitle}
          hidden={hidden}
          statusOf={statusOf}
          activeSection={current?.key || null}
          onSelectSection={selectSection}
          activeKey={activeKey}
          onJump={jumpTo}
          search={search}
          onSearch={setSearch}
          filters={filters}
          filter={filter}
          onFilter={k => setFilter(k as FilterKey)}
          filtered={filtered}
          answeredCount={answeredCount}
          totalCount={allFlat.length}
          subtitle={`${sections.length} section${sections.length === 1 ? '' : 's'}`}
        />

        <main style={{ flex: 1, minWidth: 0 }}>
          <div style={{ width: '90%', margin: '0 auto', padding: '28px 0 80px' }}>
            {filtered ? (
              <FilteredList p={p} rows={filtered} sectionTitle={sectionTitle} onClear={() => { setSearch(''); setFilter('all'); }}
                renderRow={r => card(r.item, r.depth ? 1 : 0, r.depth ? parentCodeOf(r.item.question_key) : null)} />
            ) : current ? (
              <>
                <SectionHeader p={p} title={sectionTitle(current.key)} index={currentIdx} count={sections.length} done={currentDone} total={currentFlat.length} />
                {current.nodes.length ? current.nodes.map(node => renderNode(node, 0, null)) : <EmptySection p={p} />}
                <SectionPager
                  p={p}
                  font={sans}
                  prevTitle={currentIdx > 0 ? sectionTitle(sections[currentIdx - 1].key) : null}
                  nextTitle={currentIdx < sections.length - 1 ? sectionTitle(sections[currentIdx + 1].key) : null}
                  onPrev={() => selectSection(sections[currentIdx - 1].key)}
                  onNext={() => selectSection(sections[currentIdx + 1].key)}
                  end={<span style={{ fontSize: 12.5, color: p.muted }}>End of the questionnaire</span>}
                />
              </>
            ) : (
              <div style={{ fontSize: 13, color: p.muted, textAlign: 'center', padding: 48 }}>This questionnaire has no questions yet.</div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Preview has no vendor/response for a real upload to attach to, so this only records the picked
// file's name locally -- the same "nothing here is saved" rule as the rest of the page.
function PreviewEvidence({ p, itemKey, files, onAdd, onRemove }: {
  p: QPalette; itemKey: string; files: string[]; onAdd: (fileName: string) => void; onRemove: (idx: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 14 }}>
      {files.map((f, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 99, fontSize: 12,
          border: `1px solid ${p.line}`, background: p.surface2, color: p.ink,
        }}>
          {f}
          <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f}`} style={{ background: 'none', border: 'none', color: p.faint, cursor: 'pointer', fontSize: 13, padding: '0 3px' }}>×</button>
        </span>
      ))}
      <input key={itemKey} ref={inputRef} type="file" style={{ display: 'none' }}
        onChange={e => { const file = e.target.files?.[0]; if (file) onAdd(file.name); e.target.value = ''; }} />
      <button type="button" onClick={() => inputRef.current?.click()} style={{
        background: 'none', border: 'none', padding: 0, color: p.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer',
      }}>
        + Attach evidence
      </button>
    </div>
  );
}
